import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Search, Bell, LogOut, Menu, UserCircle, Settings, Users, CreditCard, Package, PackageOpen, RotateCcw, AlertTriangle, Clock, Store as StoreIcon, Warehouse, Truck, CornerDownLeft, BoxIcon, MessageSquare, Receipt } from 'lucide-react';
export type ViewMode = 'operator' | 'client';
export type SearchTarget = {
  contextId?: string;
  activeItem?: string;
  openShipmentId?: string;
  openTicketId?: string;
  clientSection?: 'panoramica' | 'spedizioni' | 'resi' | 'ticket';
  clientShipmentId?: string;
  clientTicketId?: string;
};
type Props = {
  viewMode: ViewMode;
  onMenuClick?: () => void;
  onNotificationsClick?: () => void;
  onLogout?: () => void;
  onProfileClick?: () => void;
  onAccountNavigate?: (label: string) => void;
  onSearchSelect?: (target: SearchTarget) => void;
};
type Category = 'Spedizioni' | 'Ticket' | 'Anomalie' | 'Ritardi' | 'Giacenze' | 'Resi' | 'Store' | 'Magazzini' | 'Vettori' | 'Fatture' | 'Colli';
type CatalogEntry = {
  id: string;
  label: string;
  sub?: string;
  category: Category;
  target: SearchTarget;
};
const ADMIN_CATALOG: CatalogEntry[] = [
// Spedizioni
{
  id: 'SH-7H2K9P',
  label: 'SH-7H2K9P',
  sub: 'Store Milano Duomo · FedEx · Da firmare',
  category: 'Spedizioni',
  target: {
    contextId: 'shipments',
    activeItem: 'Tutte le spedizioni',
    openShipmentId: 'SH-7H2K9P'
  }
}, {
  id: 'SH-3M8N1Q',
  label: 'SH-3M8N1Q',
  sub: 'Store Roma Termini · FedEx · In transito',
  category: 'Spedizioni',
  target: {
    contextId: 'shipments',
    activeItem: 'Tutte le spedizioni',
    openShipmentId: 'SH-3M8N1Q'
  }
}, {
  id: 'SH-9F4D2R',
  label: 'SH-9F4D2R',
  sub: 'Store Firenze Centro · FedEx',
  category: 'Spedizioni',
  target: {
    contextId: 'shipments',
    activeItem: 'Tutte le spedizioni',
    openShipmentId: 'SH-9F4D2R'
  }
},
// Ticket
{
  id: 'TK-9921',
  label: 'TK-9921',
  sub: 'Ritardo nella consegna · In lavorazione',
  category: 'Ticket',
  target: {
    contextId: 'tickets',
    activeItem: 'Tutti i ticket',
    openTicketId: 'TK-9921'
  }
}, {
  id: 'TK-9842',
  label: 'TK-9842',
  sub: 'Richiesta etichetta di reso · Risolto',
  category: 'Ticket',
  target: {
    contextId: 'tickets',
    activeItem: 'Tutti i ticket',
    openTicketId: 'TK-9842'
  }
}, {
  id: 'tickets-da-approvare',
  label: 'Resi da approvare',
  sub: 'Tutti i resi in attesa di approvazione',
  category: 'Ticket',
  target: {
    contextId: 'tickets',
    activeItem: 'Resi da approvare'
  }
},
// Anomalie
{
  id: 'anomalie-list',
  label: 'Anomalie aperte',
  sub: 'Tutte le anomalie attive',
  category: 'Anomalie',
  target: {
    contextId: 'shipments',
    activeItem: 'Anomalie'
  }
},
// Ritardi
{
  id: 'ritardi-list',
  label: 'Spedizioni in ritardo',
  sub: 'Spedizioni oltre ETA',
  category: 'Ritardi',
  target: {
    contextId: 'shipments',
    activeItem: 'Ritardi'
  }
},
// Giacenze
{
  id: 'giacenze-list',
  label: 'Spedizioni in giacenza',
  sub: 'Colli fermi presso il vettore',
  category: 'Giacenze',
  target: {
    contextId: 'shipments',
    activeItem: 'Giacenze'
  }
},
// Store
{
  id: 'store-milano-duomo',
  label: 'Store Milano Duomo',
  sub: 'Milano · Galleria Vittorio Emanuele',
  category: 'Store',
  target: {
    contextId: 'stores',
    activeItem: 'Negozi'
  }
}, {
  id: 'store-roma-termini',
  label: 'Store Roma Termini',
  sub: 'Roma · Via del Corso 88',
  category: 'Store',
  target: {
    contextId: 'stores',
    activeItem: 'Negozi'
  }
}, {
  id: 'store-firenze-centro',
  label: 'Store Firenze Centro',
  sub: 'Firenze · Via Roma 24',
  category: 'Store',
  target: {
    contextId: 'stores',
    activeItem: 'Negozi'
  }
}, {
  id: 'store-torino',
  label: 'Store Torino Porta Nuova',
  sub: 'Torino · Via Roma',
  category: 'Store',
  target: {
    contextId: 'stores',
    activeItem: 'Negozi'
  }
},
// Magazzini
{
  id: 'mag-roma',
  label: 'Magazzino Roma',
  sub: 'Hub centrale',
  category: 'Magazzini',
  target: {
    contextId: 'shipments',
    activeItem: 'Magazzini'
  }
}, {
  id: 'mag-bologna',
  label: 'Magazzino Bologna',
  sub: 'Hub Nord',
  category: 'Magazzini',
  target: {
    contextId: 'shipments',
    activeItem: 'Magazzini'
  }
},
// Vettori
{
  id: 'fedex',
  label: 'FedEx',
  sub: 'Performance vettore',
  category: 'Vettori',
  target: {
    contextId: 'carriers',
    activeItem: 'Performance'
  }
}, {
  id: 'brt',
  label: 'BRT',
  sub: 'Performance vettore',
  category: 'Vettori',
  target: {
    contextId: 'carriers',
    activeItem: 'Performance'
  }
}, {
  id: 'gls',
  label: 'GLS',
  sub: 'Performance vettore',
  category: 'Vettori',
  target: {
    contextId: 'carriers',
    activeItem: 'Performance'
  }
}, {
  id: 'ups',
  label: 'UPS',
  sub: 'Performance vettore',
  category: 'Vettori',
  target: {
    contextId: 'carriers',
    activeItem: 'Performance'
  }
}, {
  id: 'sda',
  label: 'SDA',
  sub: 'Performance vettore',
  category: 'Vettori',
  target: {
    contextId: 'carriers',
    activeItem: 'Performance'
  }
},
// Fatture
{
  id: 'fatture-list',
  label: 'Verifica fatture',
  sub: 'Controllo fatture vettori',
  category: 'Fatture',
  target: {
    contextId: 'carriers',
    activeItem: 'Verifica Fatture'
  }
},
// Colli
{
  id: 'COL-001',
  label: 'Scatola scarpe standard',
  sub: 'COL-001 · 35×22×13 cm',
  category: 'Colli',
  target: {
    contextId: 'shipments',
    activeItem: 'Colli'
  }
}, {
  id: 'COL-002',
  label: 'Borsa media',
  sub: 'COL-002 · 40×30×15 cm',
  category: 'Colli',
  target: {
    contextId: 'shipments',
    activeItem: 'Colli'
  }
}, {
  id: 'COL-003',
  label: 'Abito appeso',
  sub: 'COL-003 · 110×60×8 cm',
  category: 'Colli',
  target: {
    contextId: 'shipments',
    activeItem: 'Colli'
  }
}];
const CUSTOMER_CATALOG: CatalogEntry[] = [
// Spedizioni
{
  id: 'SH-2840-119',
  label: 'SH-2840-119',
  sub: 'Galleria Vittorio Emanuele, Milano · FedEx · Anomalia',
  category: 'Spedizioni',
  target: {
    clientSection: 'spedizioni',
    clientShipmentId: 'SH-2840-119'
  }
}, {
  id: 'SH-2840-118',
  label: 'SH-2840-118',
  sub: 'Via del Corso 88, Roma · FedEx · In consegna',
  category: 'Spedizioni',
  target: {
    clientSection: 'spedizioni',
    clientShipmentId: 'SH-2840-118'
  }
}, {
  id: 'SH-2840-117',
  label: 'SH-2840-117',
  sub: 'Via Roma 24, Torino · GLS · In transito',
  category: 'Spedizioni',
  target: {
    clientSection: 'spedizioni',
    clientShipmentId: 'SH-2840-117'
  }
}, {
  id: 'SH-2840-116',
  label: 'SH-2840-116',
  sub: 'Via Etnea 102, Catania · UPS · Consegnata',
  category: 'Spedizioni',
  target: {
    clientSection: 'spedizioni',
    clientShipmentId: 'SH-2840-116'
  }
}, {
  id: 'SH-2840-115',
  label: 'SH-2840-115',
  sub: 'Via Toledo 145, Napoli · SDA · In partenza',
  category: 'Spedizioni',
  target: {
    clientSection: 'spedizioni',
    clientShipmentId: 'SH-2840-115'
  }
},
// Ticket
{
  id: 'TK-9921-c',
  label: 'TK-9921',
  sub: 'Ritardo nella consegna · In lavorazione',
  category: 'Ticket',
  target: {
    clientSection: 'ticket',
    clientTicketId: 'TK-9921'
  }
}, {
  id: 'TK-9842-c',
  label: 'TK-9842',
  sub: 'Richiesta etichetta di reso · Risolto',
  category: 'Ticket',
  target: {
    clientSection: 'ticket',
    clientTicketId: 'TK-9842'
  }
},
// Resi
{
  id: 'resi-list',
  label: 'I miei resi',
  sub: 'Tutte le richieste di reso',
  category: 'Resi',
  target: {
    clientSection: 'resi'
  }
}];
const CATEGORY_ICONS: Record<Category, React.ElementType> = {
  Spedizioni: Package,
  Ticket: MessageSquare,
  Anomalie: AlertTriangle,
  Ritardi: Clock,
  Giacenze: PackageOpen,
  Store: StoreIcon,
  Magazzini: Warehouse,
  Vettori: Truck,
  Colli: BoxIcon,
  Fatture: Receipt,
  Resi: RotateCcw
};
const CATEGORY_ORDER: Category[] = ['Spedizioni', 'Ticket', 'Anomalie', 'Ritardi', 'Giacenze', 'Resi', 'Store', 'Magazzini', 'Vettori', 'Fatture', 'Colli'];
export function TopBar({
  viewMode,
  onMenuClick,
  onNotificationsClick,
  onLogout,
  onProfileClick,
  onAccountNavigate,
  onSearchSelect
}: Props) {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setFocused(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const handleProfile = () => {
    setOpen(false);
    onProfileClick?.();
  };
  const handleLogout = () => {
    setOpen(false);
    onLogout?.();
  };
  const catalog = viewMode === 'client' ? CUSTOMER_CATALOG : ADMIN_CATALOG;
  // Filtered + grouped search results
  const {
    flatResults,
    grouped
  } = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return {
      flatResults: [] as CatalogEntry[],
      grouped: [] as {
        category: Category;
        items: CatalogEntry[];
      }[]
    };
    // Match items whose label or sub or id starts with / contains the query
    const matches = catalog.filter(e => {
      const hay = `${e.label} ${e.sub ?? ''} ${e.id}`.toLowerCase();
      return hay.split(/\s+/).some(token => token.startsWith(q)) || hay.includes(q);
    });
    // Group preserving CATEGORY_ORDER
    const byCat: Record<string, CatalogEntry[]> = {};
    matches.forEach(m => {
      ;
      (byCat[m.category] ||= []).push(m);
    });
    const grouped = CATEGORY_ORDER.filter(c => byCat[c]?.length).map(c => ({
      category: c,
      items: byCat[c]
    }));
    const flat = grouped.flatMap(g => g.items);
    return {
      flatResults: flat,
      grouped
    };
  }, [query, catalog]);
  useEffect(() => {
    setActiveIdx(0);
  }, [query]);
  const showDropdown = focused && query.trim().length > 0;
  const selectResult = (entry: CatalogEntry) => {
    onSearchSelect?.(entry.target);
    setQuery('');
    setFocused(false);
    inputRef.current?.blur();
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown || flatResults.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => (i + 1) % flatResults.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => (i - 1 + flatResults.length) % flatResults.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const chosen = flatResults[activeIdx];
      if (chosen) selectResult(chosen);
    } else if (e.key === 'Escape') {
      setFocused(false);
      inputRef.current?.blur();
    }
  };
  let runningIdx = 0;
  return <header className="sticky top-0 z-10 bg-[#0A0A0A]/95 backdrop-blur border-b border-[#1F1F1F] px-4 lg:px-8 py-3 lg:py-5" data-id="element-4062">
      <div className="flex items-center gap-3" data-id="element-4063">
        <button onClick={onMenuClick} aria-label="Apri menu" className="md:hidden h-10 w-10 rounded-lg flex items-center justify-center text-[#FAFAFA] hover:bg-[#181818] focus:outline-none focus:ring-2 focus:ring-[#D4FF3A]/30 transition" data-id="element-4064">
          <Menu className="h-5 w-5" data-id="element-4065" />
        </button>

        <div className="relative hidden lg:block" ref={searchRef} data-id="element-4066">
          <Search className="h-4 w-4 text-[#6B6B6B] absolute left-3 top-1/2 -translate-y-1/2 z-10 pointer-events-none" data-id="element-4067" />
          <input ref={inputRef} type="text" value={query} onChange={e => setQuery(e.target.value)} onFocus={() => setFocused(true)} onKeyDown={handleKeyDown} placeholder="Cerca spedizioni, vettori, store, colli..." className={`pl-9 pr-3 py-2 rounded-lg bg-[#181818] border text-sm text-[#FAFAFA] placeholder:text-[#6B6B6B] focus:outline-none transition-all duration-300 ease-out ${focused ? 'w-[460px] border-[#D4FF3A]/40 ring-2 ring-[#D4FF3A]/20' : 'w-64 border-[#262626] hover:border-[#333]'}`} data-id="element-4068" />

          {/* Dropdown */}
          {showDropdown && <div className="absolute left-0 top-[calc(100%+8px)] w-[460px] z-30 rounded-xl bg-[#0F0F0F] border border-[#262626] shadow-2xl overflow-hidden" data-id="element-4069">
              {grouped.length === 0 ? <div className="p-6 text-center text-sm text-[#6B6B6B]" data-id="element-4070">
                  Nessun risultato per "{query}"
                </div> : <>
                  <div className="max-h-[420px] overflow-y-auto" data-id="element-4071">
                    {grouped.map(g => {
                const Icon = CATEGORY_ICONS[g.category];
                return <div key={g.category} data-id="element-4072">
                          <div className="px-3 pt-3 pb-1.5 text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium bg-[#0A0A0A]/60 sticky top-0 z-10 flex items-center gap-1.5" data-id="element-4073">
                            <Icon className="h-3 w-3" data-id="element-4074" />
                            {g.category}
                            <span className="text-[#3F3F3F] font-mono ml-1" data-id="element-4075">
                              {g.items.length}
                            </span>
                          </div>
                          <ul data-id="element-4076">
                            {g.items.map(item => {
                      const idx = runningIdx++;
                      const isActive = idx === activeIdx;
                      return <li key={`${item.category}-${item.id}`} data-id="element-4077">
                                  <button type="button" onMouseEnter={() => setActiveIdx(idx)} onMouseDown={e => {
                          e.preventDefault();
                          selectResult(item);
                        }} className={`w-full text-left flex items-center gap-3 px-3 py-2 transition-colors ${isActive ? 'bg-[#181818]' : 'hover:bg-[#181818]'}`} data-id="element-4078">
                                    <div className={`h-7 w-7 rounded-lg border flex items-center justify-center shrink-0 ${isActive ? 'border-[#D4FF3A]/40 bg-[#D4FF3A]/5' : 'border-[#262626] bg-[#0A0A0A]'}`} data-id="element-4079">
                                      <Icon className={`h-3.5 w-3.5 ${isActive ? 'text-[#D4FF3A]' : 'text-[#9A9A9A]'}`} data-id="element-4080" />
                                    </div>
                                    <div className="flex-1 min-w-0" data-id="element-4081">
                                      <div className="text-sm font-medium text-[#FAFAFA] truncate" data-id="element-4082">
                                        <HighlightedText text={item.label} query={query} data-id="element-4083" />
                                      </div>
                                      {item.sub && <div className="text-[11px] text-[#6B6B6B] truncate" data-id="element-4084">
                                          {item.sub}
                                        </div>}
                                    </div>
                                    {isActive && <CornerDownLeft className="h-3.5 w-3.5 text-[#6B6B6B] shrink-0" data-id="element-4085" />}
                                  </button>
                                </li>;
                    })}
                          </ul>
                        </div>;
              })}
                  </div>
                  <div className="border-t border-[#1F1F1F] px-3 py-2 flex items-center justify-between text-[10px] text-[#6B6B6B]" data-id="element-4086">
                    <div className="flex items-center gap-3" data-id="element-4087">
                      <span data-id="element-4088">
                        <kbd className="px-1 py-0.5 rounded bg-[#181818] border border-[#262626] font-mono" data-id="element-4089">
                          ↑↓
                        </kbd>{' '}
                        naviga
                      </span>
                      <span data-id="element-4090">
                        <kbd className="px-1 py-0.5 rounded bg-[#181818] border border-[#262626] font-mono" data-id="element-4091">
                          ↵
                        </kbd>{' '}
                        seleziona
                      </span>
                      <span data-id="element-4092">
                        <kbd className="px-1 py-0.5 rounded bg-[#181818] border border-[#262626] font-mono" data-id="element-4093">
                          esc
                        </kbd>{' '}
                        chiudi
                      </span>
                    </div>
                    <span className="font-mono" data-id="element-4094">
                      {flatResults.length}{' '}
                      {flatResults.length === 1 ? 'risultato' : 'risultati'}
                    </span>
                  </div>
                </>}
            </div>}
        </div>

        <div className="flex items-center gap-2 ml-auto" data-id="element-4095">
          <button onClick={onNotificationsClick} aria-label="Notifiche" className="relative p-2 rounded-lg bg-[#181818] border border-[#262626] text-[#9A9A9A] hover:text-[#FAFAFA] hover:border-[#333] focus:outline-none focus:ring-2 focus:ring-[#D4FF3A]/30" data-id="element-4096">
            <Bell className="h-[18px] w-[18px]" data-id="element-4097" />
            <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-[#D4FF3A]" data-id="element-4098" />
          </button>

          <div className="relative" ref={popoverRef} data-id="element-4099">
            <button onClick={() => setOpen(v => !v)} aria-label="Profilo utente" aria-haspopup="menu" aria-expanded={open} className="h-9 w-9 rounded-full overflow-hidden focus:outline-none focus:ring-2 focus:ring-[#D4FF3A]/40 ring-1 ring-[#1F1F1F] hover:ring-[#2A2A2A] transition" data-id="element-4100">
              <img src={viewMode === 'client' ? 'https://cdn.magicpatterns.com/uploads/nf5ZnnY1RSyLrbNcTptaVQ/125232908-a8708312-6fb9-4d66-9ab0-67e98ab1edfb.jpg' : 'https://cdn.magicpatterns.com/uploads/ov9xs5AWWQhM5K61dbweFz/07EB67B8-43C6-4108-82D7-4E8C2BB23818.png'} alt={viewMode === 'client' ? 'Store Milano Duomo' : 'Marco Rossi'} className="h-full w-full object-cover" data-id="element-4101" />
            </button>

            {open && <div className="absolute right-0 top-[calc(100%+8px)] w-[260px] z-30 rounded-xl bg-[#181818] border border-[#262626] shadow-2xl overflow-hidden" data-id="element-4102">
                {/* User info */}
                <div className="p-4 border-b border-[#262626] flex items-center gap-3" data-id="element-4103">
                  <div className="h-10 w-10 rounded-full overflow-hidden ring-1 ring-[#262626] shrink-0" data-id="element-4104">
                    <img src={viewMode === 'client' ? 'https://cdn.magicpatterns.com/uploads/nf5ZnnY1RSyLrbNcTptaVQ/125232908-a8708312-6fb9-4d66-9ab0-67e98ab1edfb.jpg' : 'https://cdn.magicpatterns.com/uploads/ov9xs5AWWQhM5K61dbweFz/07EB67B8-43C6-4108-82D7-4E8C2BB23818.png'} alt={viewMode === 'client' ? 'Store Milano Duomo' : 'Marco Rossi'} className="h-full w-full object-cover" data-id="element-4105" />
                  </div>
                  <div className="min-w-0" data-id="element-4106">
                    <div className="text-sm font-semibold text-[#FAFAFA] truncate" data-id="element-4107">
                      {viewMode === 'client' ? 'Store Milano Duomo' : 'Marco Rossi'}
                    </div>
                    <div className="text-xs text-[#9A9A9A] truncate" data-id="element-4108">
                      {viewMode === 'client' ? 'customer@aisac.io' : 'admin@aisac.io'}
                    </div>
                  </div>
                </div>

                {/* Menu */}
                <div className="p-2" data-id="element-4109">
                  <button onClick={handleProfile} className="w-full flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-[#222] transition-colors text-left text-sm text-[#FAFAFA]" data-id="element-4110">
                    <UserCircle className="h-4 w-4 text-[#9A9A9A]" data-id="element-4111" />
                    <span data-id="element-4112">Profilo</span>
                  </button>
                </div>

                {viewMode === 'operator' && onAccountNavigate && <div className="border-t border-[#262626] p-2" data-id="element-4113">
                    <div className="px-2 pb-1 text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium" data-id="element-4114">
                      Account
                    </div>
                    {[{
                label: 'Impostazioni',
                icon: Settings
              }, {
                label: 'Team',
                icon: Users
              }, {
                label: 'Notifiche',
                icon: Bell
              }].map(({
                label,
                icon: Icon
              }) => <button key={label} onClick={() => {
                setOpen(false);
                onAccountNavigate(label);
              }} className="w-full flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-[#222] transition-colors text-left text-sm text-[#FAFAFA]" data-id="element-4115">
                        <Icon className="h-4 w-4 text-[#9A9A9A]" data-id="element-4116" />
                        <span data-id="element-4117">{label}</span>
                      </button>)}
                  </div>}

                <div className="border-t border-[#262626] p-2" data-id="element-4118">
                  <button onClick={handleLogout} className="w-full flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-[#222] transition-colors text-left text-sm text-[#9A9A9A] hover:text-[#FAFAFA]" data-id="element-4119">
                    <LogOut className="h-3.5 w-3.5" data-id="element-4120" />
                    <span data-id="element-4121">Esci</span>
                  </button>
                </div>
              </div>}
          </div>
        </div>
      </div>
    </header>;
}
function HighlightedText({
  text,
  query
}: {
  text: string;
  query: string;
}) {
  const q = query.trim();
  if (!q) return <>{text}</>;
  const lower = text.toLowerCase();
  const ql = q.toLowerCase();
  const idx = lower.indexOf(ql);
  if (idx === -1) return <>{text}</>;
  return <>
      {text.slice(0, idx)}
      <span className="text-[#D4FF3A]" data-id="element-4122">{text.slice(idx, idx + q.length)}</span>
      {text.slice(idx + q.length)}
    </>;
}